import { CustomSubscriberSignalFormatView } from "../monitorDetailsInformation/CustomSubscriberSignalFormatView";
import { clientMappingToObject, notificationTypeToMappingType } from "@avenueops/frontend-shared";
import { ClientZendeskIntegrationData, WebhookIntegrationData, ZendeskIntegrationData } from "@avenueops/shared-types";
import { Badge, Checkbox, DropdownMultiSelect, DropdownSelect, InputOption, Pill, TextInput, Toggle } from "@avenueops/web-components";
import { IntegrationType, NotificationType, Team } from "@prisma/client";
import * as Popover from "@radix-ui/react-popover";
import { getTicketingIntegrationData } from "components/notifications/utils";
import parsePhoneNumber from "libphonenumber-js";
import { useMemo } from "react";
import { getListRecipientValueOptions, getTicketingIntegrations } from "utils/mergeTicketingUtils";
import { trpc, trpc2 } from "utils/trpc";

interface Props {
  recipient: any;
}

export function MonitorSubscriberBadge({ recipient }: Props): JSX.Element {
  const { data: slackRecipientOptions, isLoading: isLoadingSlackRecipients } = trpc2.v2.slack.getRecipients.useQuery({
    integrationId: recipient.integrationId,
  });
  const flatSlackRecipientOptions = isLoadingSlackRecipients ? [] : slackRecipientOptions?.flatMap((group: any) => group.options);

  const { data: integrations, isLoading: isLoadingIntegrations } = trpc.integrations.list.useQuery();
  const { data: sendbirdCustomerOptions } = trpc2.v2.sendbird.getCustomers.useQuery();

  const { isLoading: isLoadingMemberships, data: memberships } = trpc.organizationMemberships.list.useQuery();
  const { isLoading: isLoadingTeams, data: teams } = trpc.teams.list.useQuery();

  const sendgridIntegration = integrations ? integrations.find((value: any) => value.type === IntegrationType.SENDGRID) : undefined;
  const twilioIntegration = integrations ? integrations.find((value: any) => value.type === IntegrationType.TWILIO) : undefined;

  const { data: notionParentOptions } = trpc2.v2.notion.getNotionPagesAndDatabases.useQuery(
    { integrationId: recipient.integrationId },
    { enabled: recipient.manualType === NotificationType.NOTION && !!recipient.integrationId },
  );

  const zendeskIntegrations = useMemo(() => {
    return integrations ? integrations.filter((value: any) => value.type === IntegrationType.ZENDESK) : undefined;
  }, [integrations]);
  const webhookIntegrations = useMemo(() => {
    return integrations ? integrations.filter((value: any) => value.type === IntegrationType.WEBHOOK) : undefined;
  }, [integrations]);
  const notionIntegrations = useMemo(() => {
    return integrations ? integrations.filter((value: any) => value.type === IntegrationType.NOTION) : undefined;
  }, [integrations]);
  const intercomIntegrations = useMemo(() => {
    return integrations ? integrations.filter((value: any) => value.type === IntegrationType.INTERCOM) : undefined;
  }, [integrations]);
  const sendbirdIntegrations = useMemo(() => {
    return integrations ? integrations.filter((value: any) => value.type === IntegrationType.SENDBIRD) : undefined;
  }, [integrations]);
  const slackIntegrations = useMemo(() => {
    return integrations ? integrations.filter((value: any) => value.type === IntegrationType.SLACK) : undefined;
  }, [integrations]);

  const defaultChoices = [
    { label: "SMS Message", value: NotificationType.SMS },
    { label: "WhatsApp", value: NotificationType.WHATSAPP },
    { label: "Slack", value: NotificationType.SLACK },
    { label: "Notion", value: NotificationType.NOTION },
    { label: "Webhook", value: NotificationType.WEBHOOK },
    { label: "Email", value: NotificationType.EMAIL },
    { label: "Telegram", value: NotificationType.TELEGRAM },
    { label: "Customer.io", value: NotificationType.CUSTOMER_IO },
    { label: "Asana", value: NotificationType.ASANA },
    { label: "Jira", value: NotificationType.JIRA },
    { label: "Trello", value: NotificationType.TRELLO },
    { label: "Linear", value: NotificationType.LINEAR },
    { label: "Height", value: NotificationType.HEIGHT },
    { label: "Front", value: NotificationType.FRONT },
    { label: "Intercom (Beta)", value: NotificationType.INTERCOM },
    { label: "Zendesk", value: NotificationType.ZENDESK },
    { label: "Sendbird", value: NotificationType.SENDBIRD },
  ];

  // Merge Ticketing Integrations
  const mergeIntegrations = integrations ? integrations.filter((value: any) => value.type === IntegrationType.MERGE_TICKETING) : undefined;
  const asanaInstances = getTicketingIntegrations(mergeIntegrations, NotificationType.ASANA);
  const linearInstances = getTicketingIntegrations(mergeIntegrations, NotificationType.LINEAR);
  const heightAppInstances = getTicketingIntegrations(mergeIntegrations, NotificationType.HEIGHT);
  const jiraInstances = getTicketingIntegrations(mergeIntegrations, NotificationType.JIRA);
  const trelloInstances = getTicketingIntegrations(mergeIntegrations, NotificationType.TRELLO);
  const trelloRecipientOptions = getListRecipientValueOptions(trelloInstances);
  const frontInstances = getTicketingIntegrations(mergeIntegrations, NotificationType.FRONT);
  const mergeIntegrationData = getTicketingIntegrationData(mergeIntegrations, recipient.integrationId);

  let pills: string[];
  if (recipient.manualType) {
    const notificationType = recipient.manualType as NotificationType;
    switch (notificationType) {
      case NotificationType.SMS:
      case NotificationType.WHATSAPP:
        pills = [
          `${parsePhoneNumber(recipient.recipientValue)?.formatNational() ?? recipient.recipientValue} | ${
            notificationType === NotificationType.SMS ? "SMS" : "WhatsApp"
          }`,
        ];
        break;
      case NotificationType.EMAIL:
        pills = [recipient.recipientValue];
        if (recipient.emailCCRecipient) {
          pills.push(`${recipient.emailCCRecipient} | Email`);
        }
        break;
      case NotificationType.SLACK:
        pills = isLoadingSlackRecipients
          ? ["Loading... "]
          : [
              `${
                (flatSlackRecipientOptions?.find((slackOption: InputOption) => slackOption.value === recipient.recipientValue)?.label as string) ??
                recipient.recipientValue
              } | Slack`,
            ];
        break;
      case NotificationType.NOTION:
        pills = !isLoadingIntegrations
          ? [`${notionIntegrations?.find((integration: any) => integration.integrationId === recipient.recipientValue)?.name} | Notion` ?? "Notion Workspace"]
          : ["Loading..."];
        break;

      case NotificationType.WEBHOOK:
        pills = !recipient.integrationId
          ? [recipient.recipientValue]
          : !isLoadingIntegrations
          ? [
              `${(
                webhookIntegrations?.find((integration: any) => integration.integrationId === recipient.integrationId)
                  ?.integrationData as WebhookIntegrationData
              )?.baseUrl}${recipient.recipientValue} | Webhook` ?? "Webhook Integration",
            ]
          : ["Loading"];
        break;
      case NotificationType.TELEGRAM:
        pills = [recipient.recipientValue];
        break;
      case NotificationType.CUSTOMER_IO:
        pills = ["Customer.io"];
        break;
      case NotificationType.ASANA:
        pills = !isLoadingIntegrations
          ? [`${asanaInstances?.find((integration) => integration.integrationId === recipient.integrationId)?.name} | Asana` ?? "Asana Integration"]
          : ["Loading..."];
        break;
      case NotificationType.JIRA:
        pills = !isLoadingIntegrations
          ? [`${jiraInstances?.find((integration) => integration.integrationId === recipient.integrationId)?.name} | Jira` ?? "Jira Integration"]
          : ["Loading..."];
        break;
      case NotificationType.TRELLO:
        pills = !isLoadingIntegrations
          ? [`${trelloInstances?.find((integration) => integration.integrationId === recipient.integrationId)?.name} | Trello` ?? "Trello Integration"]
          : ["Loading..."];
        break;
      case NotificationType.LINEAR:
        pills = !isLoadingIntegrations
          ? [`${linearInstances?.find((integration) => integration.integrationId === recipient.integrationId)?.name} | Linear` ?? "Linear Integration"]
          : ["Loading..."];
        break;
      case NotificationType.HEIGHT:
        pills = !isLoadingIntegrations
          ? [`${heightAppInstances?.find((integration) => integration.integrationId === recipient.integrationId)?.name} | Height` ?? "Height App Integration"]
          : ["Loading..."];
        break;
      case NotificationType.FRONT:
        pills = !isLoadingIntegrations
          ? [`${frontInstances?.find((integration) => integration.integrationId === recipient.integrationId)?.name} | Front` ?? "Front Integration"]
          : ["Loading..."];
        break;
      case NotificationType.INTERCOM:
        pills = !isLoadingIntegrations
          ? [`${intercomIntegrations?.find((integration) => integration.integrationId === recipient.integrationId)?.name} | Intercom` ?? "Intercom Integration"]
          : ["Loading..."];
        break;
      case NotificationType.ZENDESK:
        pills = !isLoadingIntegrations
          ? [
              `${(
                zendeskIntegrations?.find((integration: any) => integration.integrationId === recipient.integrationId)
                  ?.integrationData as ZendeskIntegrationData
              )?.remoteUri} | Zendesk` ?? "Zendesk Integration",
            ]
          : ["Loading..."];
        break;
      case NotificationType.SENDBIRD:
        pills = !isLoadingIntegrations
          ? [`${sendbirdCustomerOptions?.find((customer) => customer.value === recipient.recipientValue)?.label} | Sendbird` ?? "Sendbird customer"]
          : ["Loading..."];
        break;
      case NotificationType.SALESFORCE:
        pills = ["Salesforce"];
        break;
      case NotificationType.HUBSPOT:
        pills = ["HubSpot"];
        break;
    }
  } else {
    switch (recipient.recipientType) {
      case "USER":
        const user = memberships?.find((membership: any) => membership.userId === recipient.recipientId)?.user;
        pills = isLoadingMemberships ? ["Loading..."] : user ? [`${user?.firstName} ${user?.lastName}`] : ["Avenue user"];
        break;
      case "TEAM":
        const team = teams?.find((team: Team) => team.id === recipient.recipientId);
        pills = isLoadingTeams ? ["Loading..."] : team ? [`${team?.emoji} ${team.name}`] : ["Avenue team"];
        break;
      default:
        pills = [];
    }
  }

  return recipient.manualType ? (
    <div className="flex flex-wrap gap-2">
      {pills.map((pillText, index) => (
        <div key={index}>
          <Popover.Root>
            <Popover.Trigger>
              <div className="flex gap-0.5 items-start">
                <Badge key={index}>{pillText}</Badge>
                {recipient.titleFormat || recipient.messageFormat ? <div className="text-lg font-medium text-red-500">*</div> : null}
              </div>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                className="relative z-15 shadow-xl rounded-lg p-5 bg-white border border-gray-200 overflow-hidden"
                sideOffset={5}
                align={"start"}
                side={"top"}
              >
                <div className="flex flex-row gap-5 whitespace-nowrap">
                  <div className="w-[250px]">
                    <DropdownSelect name={index.toString()} disabled options={defaultChoices} title="Notification Type" selectedValue={recipient.manualType} />
                    {recipient.manualType === NotificationType.ZENDESK ||
                    recipient.manualType === NotificationType.EMAIL ||
                    recipient.manualType === NotificationType.SLACK ? (
                      <div className="mt-2">
                        <Checkbox name={"recipient.includeAttachments"} label="Include CSV attachment of results" small disabled />
                      </div>
                    ) : null}
                    {recipient.manualType === NotificationType.EMAIL && sendgridIntegration ? (
                      <div className="mt-4">
                        <Checkbox name={"recipient.useCustomIntegration"} label="Use custom Sendgrid" small defaultValue={true} disabled />
                      </div>
                    ) : null}
                    {recipient.manualType === NotificationType.SMS && twilioIntegration ? (
                      <div className="mt-4">
                        <Checkbox name={"recipient.useCustomIntegration"} label="Use custom Twilio" small defaultValue={true} disabled />
                      </div>
                    ) : null}
                  </div>
                  <div>
                    {recipient.manualType === NotificationType.SMS ? (
                      <>
                        {recipient.useCustomIntegration ? (
                          <div className="flex w-full gap-1">
                            <TextInput
                              name={"recipient.smsFrom"}
                              title="Phone Number to Send From"
                              placeholder="+14194190034"
                              defaultValue={"recipient.smsFrom"}
                              disabled
                            />
                          </div>
                        ) : null}
                        <div className="flex w-full gap-1">
                          <TextInput name={"recipient.recipientValue"} title="Phone Number to Notify" defaultValue={recipient.recipientValue} disabled />{" "}
                        </div>
                        <CustomSubscriberSignalFormatView recipient={recipient} showFormatting />
                      </>
                    ) : recipient.manualType === NotificationType.WHATSAPP ? (
                      <>
                        <TextInput name="recipientValue" title="WhatsApp Number to Notify" defaultValue={recipient.recipientValue ?? ""} disabled />
                        <CustomSubscriberSignalFormatView recipient={recipient} showFormatting />
                      </>
                    ) : recipient.manualType === NotificationType.SLACK ? (
                      <>
                        {slackRecipientOptions ? (
                          <DropdownSelect
                            name={"recipient.recipientValue"}
                            title="Slack Channel, Member ID, or Dynamic Input"
                            options={slackRecipientOptions}
                            placeholder="#general, {{channel_id}}, @U123ABC (Slack member ID)"
                            disabled
                            selectedValue={recipient.recipientValue}
                          />
                        ) : (
                          <TextInput
                            name={"recipient.recipientValue"}
                            title="Slack Channel, Member ID, or Dynamic Input"
                            placeholder="#general, {{channel_id}}, @U123ABC (Slack member ID)"
                            disabled
                            defaultValue={recipient.recipientValue}
                          />
                        )}

                        <CustomSubscriberSignalFormatView recipient={recipient} showFormatting showNotifications />
                      </>
                    ) : recipient.manualType === NotificationType.NOTION && notionIntegrations && notionIntegrations.length > 0 ? (
                      <>
                        <DropdownSelect
                          name={"recipient.integrationId"}
                          title="Notion Instance"
                          options={notionIntegrations.map((integration) => ({
                            label: integration.name ?? "Notion workspace",
                            value: integration.integrationId,
                          }))}
                          disabled
                          selectedValue={recipient.integrationId}
                        />
                        <div className="pt-5">
                          <DropdownSelect
                            name={"recipient.recipientValue"}
                            title="Notion Page or Database"
                            options={notionParentOptions ?? []}
                            disabled={true}
                            selectedValue={recipient.recipientValue}
                            placeholder={
                              notionParentOptions === undefined || notionParentOptions.length === 0
                                ? "No data for selected Notion instance"
                                : "Notion page or database"
                            }
                          />
                        </div>
                        {recipient.useMappingOverride ? (
                          <CustomSubscriberSignalFormatView recipient={recipient} showNotifications />
                        ) : (
                          <CustomSubscriberSignalFormatView recipient={recipient} showFormatting showNotifications />
                        )}
                      </>
                    ) : recipient.manualType === NotificationType.WEBHOOK ? (
                      webhookIntegrations && webhookIntegrations.length > 0 ? (
                        <div>
                          {recipient.integrationId ? (
                            <div className="grid grid-cols-5 items-end gap-x-2">
                              <div className="col-span-3">
                                <DropdownSelect
                                  name={"recipient.integrationId"}
                                  options={webhookIntegrations.map((integration) => ({
                                    label: (integration.integrationData as WebhookIntegrationData)?.baseUrl ?? "Webhook Integration",
                                    value: integration.integrationId,
                                  }))}
                                  title={"Webhook Integration"}
                                  disabled
                                  selectedValue={recipient.integrationId}
                                />
                              </div>
                              <div className="col-span-2">
                                <TextInput name={`recipientValue`} defaultValue={recipient.recipientValue} disabled title={"Optional parameters"} />
                              </div>
                            </div>
                          ) : (
                            <TextInput
                              name={"recipient.recipientValue"}
                              title={"Custom Webhook"}
                              placeholder="https://example.com/my-webhook/"
                              defaultValue={recipient.recipientValue}
                              disabled
                            />
                          )}
                        </div>
                      ) : (
                        <TextInput
                          name={"recipient.recipientValue"}
                          title="Webhook to send a POST request to"
                          placeholder="https://example.com/my-webhook/"
                          disabled
                          defaultValue={recipient.recipientValue}
                        />
                      )
                    ) : recipient.manualType === NotificationType.TELEGRAM ? (
                      <>
                        <TextInput
                          name={"recipient.recipientValue"}
                          defaultValue={recipient.recipientValue}
                          title="Telegram Chat ID to Notify"
                          placeholder="1234567890"
                          disabled
                        />
                        <CustomSubscriberSignalFormatView recipient={recipient} showFormatting />
                      </>
                    ) : recipient.manualType === NotificationType.ZENDESK && zendeskIntegrations && zendeskIntegrations.length > 0 ? (
                      <>
                        <DropdownSelect
                          name={"recipient.integrationId"}
                          title="Zendesk Integration Type"
                          selectedValue={zendeskIntegrations[0].integrationId}
                          options={zendeskIntegrations.map((integration: any) => ({
                            label: (integration.integrationData as ClientZendeskIntegrationData).remoteUri ?? "Zendesk integration",
                            value: integration.integrationId,
                          }))}
                          disabled
                        />
                        {recipient.useMappingOverride ? (
                          <CustomSubscriberSignalFormatView recipient={recipient} showNotifications />
                        ) : (
                          <CustomSubscriberSignalFormatView recipient={recipient} showFormatting showNotifications />
                        )}
                      </>
                    ) : recipient.manualType === NotificationType.SENDBIRD && sendbirdIntegrations && sendbirdIntegrations.length > 0 ? (
                      <>
                        <DropdownSelect
                          name={"recipient.recipientValue"}
                          title="Customer Id"
                          options={sendbirdCustomerOptions ?? []}
                          placeholder={sendbirdCustomerOptions === undefined ? "No data for selected Sendbird instance" : "Sendbird Customer Id"}
                          disabled
                          selectedValue={recipient.recipientValue}
                        />

                        {recipient.useMappingOverride ? (
                          <CustomSubscriberSignalFormatView recipient={recipient} showNotifications />
                        ) : (
                          <CustomSubscriberSignalFormatView recipient={recipient} showFormatting showNotifications />
                        )}
                      </>
                    ) : recipient.manualType === NotificationType.EMAIL ? (
                      <>
                        {recipient.useCustomIntegration ? (
                          <TextInput
                            disabled
                            defaultValue={recipient.emailFrom}
                            name={"recipient.emailFrom"}
                            title="Email Address to Send From"
                            placeholder="richard@piedpiper.com"
                          />
                        ) : null}

                        <DropdownMultiSelect
                          title="Email Addresses to Notify"
                          placeholder="richard@piedpiper.com"
                          selectedValues={recipient.recipientValue ? [recipient.recipientValue] : []}
                          options={recipient.recipientValue ? [{ value: recipient.recipientValue, label: recipient.recipientValue }] : []}
                          name={"recipient.recipientValue"}
                          disabled
                        />

                        <div className="pt-5">
                          <DropdownMultiSelect
                            title="Email Addresses to CC"
                            placeholder="richard@piedpiper.com"
                            selectedValues={recipient.emailCCRecipient ? [recipient.emailCCRecipient] : []}
                            options={recipient.emailCCRecipient ? [{ value: recipient.emailCCRecipient, label: recipient.emailCCRecipient }] : []}
                            name={"recipient.emailCCRecipient"}
                            disabled
                          />
                        </div>
                        <CustomSubscriberSignalFormatView showFormatting showNotifications recipient={recipient} />
                      </>
                    ) : recipient.manualType === NotificationType.LINEAR && linearInstances && linearInstances.length > 0 ? (
                      <>
                        <DropdownSelect
                          name={"recipient.integrationId"}
                          title="Integration Instance"
                          options={linearInstances.map((integration) => ({
                            label: integration.name ?? "Linear integration",
                            value: integration.integrationId,
                          }))}
                          disabled
                          selectedValue={recipient.integrationId}
                        />
                        <div className="pt-5">
                          <DropdownSelect
                            name={"recipient.recipientValue"}
                            title="Team"
                            options={mergeIntegrationData.teams.map((team) => ({
                              label: team.name,
                              value: team.id,
                            }))}
                            disabled
                            selectedValue={recipient.recipientValue}
                          />
                        </div>
                        {recipient.useMappingOverride ? (
                          <CustomSubscriberSignalFormatView recipient={recipient} showNotifications />
                        ) : (
                          <CustomSubscriberSignalFormatView recipient={recipient} showFormatting showNotifications />
                        )}
                      </>
                    ) : recipient.manualType === NotificationType.HEIGHT && heightAppInstances && heightAppInstances.length > 0 ? (
                      <>
                        <DropdownSelect
                          name={"recipient.integrationId"}
                          title="Integration Instance"
                          options={linearInstances.map((integration) => ({
                            label: integration.name ?? "Height App integration",
                            value: integration.integrationId,
                          }))}
                          disabled
                          selectedValue={recipient.integrationId}
                        />
                        <div className="pt-5">
                          <DropdownSelect
                            name={"recipient.recipientValue"}
                            title="Team"
                            options={mergeIntegrationData.teams.map((team) => ({
                              label: team.name,
                              value: team.id,
                            }))}
                            disabled
                            selectedValue={recipient.recipientValue}
                          />
                        </div>
                        {recipient.useMappingOverride ? (
                          <CustomSubscriberSignalFormatView recipient={recipient} showNotifications />
                        ) : (
                          <CustomSubscriberSignalFormatView recipient={recipient} showFormatting showNotifications />
                        )}
                      </>
                    ) : recipient.manualType === NotificationType.ASANA && asanaInstances && asanaInstances.length > 0 ? (
                      <>
                        <DropdownSelect
                          name={"recipient.integrationId"}
                          title="Integration Instance"
                          options={asanaInstances.map((integration) => ({
                            label: integration.name ?? "Asana integration",
                            value: integration.integrationId,
                          }))}
                          selectedValue={recipient.integrationId}
                          disabled
                        />
                        <div className="pt-5">
                          <DropdownSelect
                            name={"recipient.recipientValue"}
                            title="Workspace"
                            options={mergeIntegrationData.collections
                              .filter((collection) => {
                                return collection.type === "PROJECT";
                              })
                              .map((collection) => {
                                return {
                                  label: collection.name ?? "",
                                  value: collection.id,
                                };
                              })}
                            selectedValue={recipient.recipientValue}
                            disabled
                          />
                        </div>

                        {recipient.useMappingOverride ? (
                          <CustomSubscriberSignalFormatView recipient={recipient} showNotifications />
                        ) : (
                          <CustomSubscriberSignalFormatView recipient={recipient} showFormatting showNotifications />
                        )}
                      </>
                    ) : recipient.manualType === NotificationType.JIRA && jiraInstances && jiraInstances.length > 0 ? (
                      <>
                        <DropdownSelect
                          name={"recipient.integrationId"}
                          title="Integration Instance"
                          options={jiraInstances.map((integration) => ({
                            label: integration.name ?? "Jira integration",
                            value: integration.integrationId,
                          }))}
                          selectedValue={recipient.integrationId}
                          disabled
                        />

                        <div className="pt-5">
                          <DropdownSelect
                            name={"recipient.recipientValue"}
                            title="Project"
                            options={mergeIntegrationData.collections
                              .filter((collection) => {
                                return collection.type === "PROJECT";
                              })
                              .map((collection) => {
                                return {
                                  label: collection.name ?? "",
                                  value: collection.id,
                                };
                              })}
                            selectedValue={recipient.recipientValue}
                            disabled
                          />
                        </div>

                        {recipient.useMappingOverride ? (
                          <CustomSubscriberSignalFormatView recipient={recipient} showNotifications />
                        ) : (
                          <CustomSubscriberSignalFormatView recipient={recipient} showFormatting showNotifications />
                        )}
                      </>
                    ) : recipient.manualType === NotificationType.TRELLO && trelloInstances && trelloInstances.length > 0 ? (
                      <>
                        <DropdownSelect
                          name={"recipient.integrationId"}
                          title="Integration Instance"
                          options={trelloInstances.map((integration) => ({
                            label: integration.name ?? "Trello integration",
                            value: integration.integrationId,
                          }))}
                          disabled
                          selectedValue={recipient.integrationId}
                        />
                        <div className="pt-5">
                          <DropdownSelect
                            name={"recipient.recipientValue"}
                            title="Project"
                            options={trelloRecipientOptions}
                            disabled
                            selectedValue={recipient.recipientValue}
                          />
                        </div>

                        {recipient.useMappingOverride ? (
                          <CustomSubscriberSignalFormatView recipient={recipient} showNotifications />
                        ) : (
                          <CustomSubscriberSignalFormatView recipient={recipient} showFormatting showNotifications />
                        )}
                      </>
                    ) : recipient.manualType === NotificationType.FRONT && frontInstances && frontInstances.length > 0 ? (
                      <>
                        <DropdownSelect
                          name={"recipient.integrationId"}
                          title="Integration Instance"
                          options={frontInstances.map((integration) => ({
                            label: integration.name ?? "Front integration",
                            value: integration.integrationId,
                          }))}
                          selectedValue={recipient.integrationId}
                          disabled
                        />

                        <div className="pt-5">
                          <DropdownSelect
                            name={"recipient.recipientValue"}
                            title="Project"
                            options={mergeIntegrationData.collections
                              .filter((collection) => {
                                return collection.type === "PROJECT";
                              })
                              .map((collection) => {
                                return {
                                  label: collection.name ?? "",
                                  value: collection.id,
                                };
                              })}
                            disabled
                            selectedValue={recipient.recipientValue}
                          />
                        </div>
                        {recipient.useMappingOverride ? (
                          <CustomSubscriberSignalFormatView recipient={recipient} showNotifications />
                        ) : (
                          <CustomSubscriberSignalFormatView recipient={recipient} showFormatting showNotifications />
                        )}
                      </>
                    ) : recipient.manualType === NotificationType.INTERCOM && intercomIntegrations && intercomIntegrations.length > 0 ? (
                      <>
                        <DropdownSelect
                          name={"recipient.integrationId"}
                          title="Integration Instance"
                          options={intercomIntegrations.map((integration) => ({
                            label: integration.name ?? "Intercom integration",
                            value: integration.integrationId,
                          }))}
                          selectedValue={recipient.integrationId}
                          disabled
                        />
                      </>
                    ) : null}

                    {notificationTypeToMappingType(recipient.manualType) ? (
                      <div>
                        {recipient.useMappingOverride ? (
                          <div className="text-sm mt-4">
                            <Toggle disabled={true} name={`useMappingOverride`} title="Advanced Usage" defaultValue={recipient.useMappingOverride} />
                          </div>
                        ) : null}

                        {recipient.useMappingOverride && recipient.mapping ? (
                          <div className="max-w-lg whitespace-pre-wrap text-gray-500 text-sm">{JSON.stringify(recipient.mapping)}</div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        </div>
      ))}
    </div>
  ) : (
    <div>
      {pills.map((pillText, index) => (
        <Badge key={index}>{pillText}</Badge>
      ))}
    </div>
  );
}
